/**
 * The stock catalogue: exercises that are always there.
 *
 * Without it the app has nothing to offer on the first day — no photo yet, no
 * text written, no mistake known. And it runs dry again as soon as everything
 * has been practised: „alles geübt" is honest, but it is not an answer to a
 * child who wants to practise now.
 *
 * The sentences are written by hand, not fetched from the model. They cost
 * nothing, work offline and are the same for everybody — no request, no key,
 * no waiting. Every one of them is checked: exactly one mistake, and `wrong`
 * against `right` differs in exactly one thing.
 *
 * They are the FALLBACK, never the main thing. What Elmar got wrong himself
 * weighs more than any invented sentence, so his own mistakes always come
 * first; the stock fills in behind them.
 */

import rows from './stock.json'
import { kindFromDiff, strategyFor, type Issue } from './exercises.js'

/**
 * One row of the catalogue: the sentence, the wrong word, the right one.
 *
 * Three more, all optional, for the rows written since the class test: the
 * kind where the comparison would get it wrong („wider → wieder" looks like
 * ie/i and is a mix-up), the strategy, and the tip that applies it to the
 * word. The older rows have none, and get the strategy their kind usually
 * calls for.
 */
export type Row = [
  sentence: string, wrong: string, right: string,
  kind?: string, strategy?: string, tip?: string,
]

/**
 * The kind of mistake a row practises.
 *
 * Derived, not written down: the same comparison the app makes for its own
 * findings. That way the catalogue cannot drift apart from the rules — a row
 * whose kind was mistyped would otherwise be filtered into the wrong place
 * for ever.
 *
 * Unless the row SAYS. The comparison cannot tell a mix-up of two real words
 * from a slip of the letters: „wider" against „wieder" is ie/i to it, and
 * „gelehmt" against „gelähmt" is nothing it knows. Those rows carry their
 * kind.
 *
 * Capitalisation is decided first: `kindFromDiff` lowercases both words and
 * would call „schule → Schule" a missing ending.
 */
export function kindOf([, wrong, right, kind]: Row): string {
  if (kind) return kind
  return wrong.toLowerCase() === right.toLowerCase()
    ? 'gross-klein'
    : kindFromDiff(wrong, right)
}

/** The catalogue, with its kind worked out once at startup. */
const CATALOGUE: { row: Row; kind: string }[] = (rows as Row[]).map((row) => ({
  row,
  kind: kindOf(row),
}))

/**
 * Turns a row into an issue the exercises can work with.
 *
 * `source: 'typed'` — not because it was typed, but because that is what the
 * variants need to know: a gap in the middle of a word is a decision here,
 * not a slip of the hand. The stock sentences are written, not read off
 * paper.
 */
function toIssue({ row, kind }: { row: Row; kind: string }, nr: number): Issue {
  const [sentence, wrong, right, , strategy, tip] = row
  return {
    id: `stock-${nr}`,
    wrong,
    right,
    kind,
    sentence,
    sentenceWrong: wrong,
    sentenceRight: right,
    context: sentence,
    // Corrected for the word: a one-syllable row never swings, however the
    // row was written.
    strategy: strategyFor(kind, right, strategy),
    tip: tip ?? '',
    // Sorted last by age everywhere — the stock waits behind his own
    // mistakes, however old those are.
    at: Number.MAX_SAFE_INTEGER,
    source: 'typed',
  }
}

/**
 * Stock exercises, chosen to fit.
 *
 * Whoever forgets the silent h needs more silent h, not a random sentence.
 * The kinds he really gets wrong therefore come first, and the rest follows
 * behind — so the supply never runs out, it only gets less targeted.
 *
 * For a new user nothing is known yet. Then the order is simply the
 * catalogue's own, which walks through the kinds in turn.
 *
 * @param own      His own open issues — they say which rules are worth
 *                 practising.
 * @param solved   Ids of stock exercises already done. They drop out.
 * @param wanted   How many are needed.
 */
export function stockIssues(
  own: readonly Issue[],
  solved: ReadonlySet<string>,
  wanted: number
): Issue[] {
  if (wanted <= 0) return []

  let open = CATALOGUE.map((entry, nr) => ({ entry, nr }))
    .filter(({ nr }) => !solved.has(`stock-${nr}`))

  // The whole catalogue done — then it starts again from the top.
  //
  // The alternative would be an empty app, and that is worse: a sentence
  // practised weeks ago is a fair exercise again. The solved ones are NOT
  // forgotten for that; they stay in the database, and the round simply
  // ignores them once there is nothing else left.
  if (!open.length) open = CATALOGUE.map((entry, nr) => ({ entry, nr }))

  // Grouped by kind, each group in the catalogue's own order.
  const byKind = new Map<string, typeof open>()
  for (const item of open) {
    const list = byKind.get(item.entry.kind) ?? []
    list.push(item)
    byKind.set(item.entry.kind, list)
  }

  // How often each kind turns up in his own mistakes. The more often, the
  // more it is worth practising.
  const weight = new Map<string, number>()
  for (const f of own) weight.set(f.kind, (weight.get(f.kind) ?? 0) + 1)

  // The kinds he really gets wrong first, the rest behind them — but ALL of
  // them, and taken in turn rather than in blocks.
  //
  // Two things were wrong with a plain sort. A new user, for whom no kind has
  // any weight, got the first twelve rows of the catalogue, and since it is
  // written kind by kind that meant twelve capitalisation sentences. And a
  // child who mostly forgets the silent h got ten silent h in a row — the
  // right rule, but nothing else at all.
  //
  // Dealt round-robin instead: one from the most needed kind, one from the
  // next, and so on from the top again. What he needs comes up more often,
  // because a needed kind is dealt from more than once per round, and still
  // nothing is practised alone.
  const order = [...byKind.keys()].sort((a, b) => {
    const wa = weight.get(a) ?? 0
    const wb = weight.get(b) ?? 0
    if (wa !== wb) return wb - wa
    // Same weight: the catalogue's order, so the result stays reproducible.
    return (byKind.get(a)?.[0]?.nr ?? 0) - (byKind.get(b)?.[0]?.nr ?? 0)
  })

  const out: Issue[] = []
  const taken = new Map<string, number>()
  while (out.length < wanted) {
    let dealtThisRound = 0
    for (const kind of order) {
      if (out.length >= wanted) break
      const list = byKind.get(kind) ?? []
      const at = taken.get(kind) ?? 0
      const item = list[at]
      if (!item) continue
      taken.set(kind, at + 1)
      out.push(toIssue(item.entry, item.nr))
      dealtThisRound++
      // A kind he really gets wrong is dealt from twice per round, so it
      // turns up about twice as often without crowding the others out.
      if ((weight.get(kind) ?? 0) > 0 && out.length < wanted) {
        const second = list[at + 1]
        if (second) {
          taken.set(kind, at + 2)
          out.push(toIssue(second.entry, second.nr))
          dealtThisRound++
        }
      }
    }
    // Every kind exhausted — the catalogue has no more to give.
    if (!dealtThisRound) break
  }

  return out
}

/** How many rows the catalogue holds — for the tests and the tools. */
export const STOCK_SIZE = CATALOGUE.length

/**
 * Which stock exercises have been done.
 *
 * In `localStorage` AND in the database. The local copy is the cache, as
 * everywhere in this app: it is there before the first sync answers, and it
 * works when there is no network. What counts is the row in `stock_solved`.
 *
 * It was local only at first, and that was wrong for exactly the case the
 * exercises were built for: Elmar practises on the phone at his mother's and
 * on the PC here. The catalogue would run through twice, showing him the same
 * sentences again while two hundred unused ones lay below them.
 *
 * The merge is the simplest one there is — a set that only grows. Whoever has
 * practised a sentence has practised it; no device may take that back.
 */
const SOLVED_KEY = 'kritzl.stock'

export function solvedStock(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(SOLVED_KEY)
    if (!raw) return new Set()
    const list = JSON.parse(raw) as unknown
    return Array.isArray(list) ? new Set(list.filter((x) => typeof x === 'string')) : new Set()
  } catch {
    // Private mode, blocked storage, a broken entry. The sync brings the set
    // back down on the next round; until then the catalogue repeats itself,
    // which is better than not practising.
    return new Set()
  }
}

/** Forgets the local copy. The sync brings the right set down again. */
export function forgetStock(): void {
  try {
    localStorage.removeItem(SOLVED_KEY)
  } catch {}
}

/** Remembers that a stock exercise was mastered. The sync carries it up. */
export function markStockSolved(id: string): void {
  write([...solvedStock(), id])
}

/**
 * Takes down what another device has solved.
 *
 * @returns true when something new arrived — then the round is rebuilt, so a
 *          sentence solved on the phone does not turn up here again.
 */
export function mergeStockSolved(ids: readonly string[]): boolean {
  const here = solvedStock()
  const added = ids.filter((id) => typeof id === 'string' && !here.has(id))
  if (!added.length) return false
  // Only report a change when one was really stored. With blocked storage the
  // read comes back empty every time, so everything would look new on every
  // sync — and the round would be rebuilt for nothing, again and again.
  return write([...here, ...added])
}

/** @returns true when it really got written. */
function write(ids: readonly string[]): boolean {
  try {
    localStorage.setItem(SOLVED_KEY, JSON.stringify([...new Set(ids)]))
    return true
  } catch {
    // Not being able to write is not a reason to stop. The exercise still
    // counts — the sync sends it up either way.
    return false
  }
}
